import { useState } from "react";
import { Link } from "react-router-dom";

import {
  FaArrowRight,
  FaChevronDown,
  FaExternalLinkAlt,
} from "react-icons/fa";

/* =========================================================
   TEAM
========================================================= */

const teamet = [
  {
    name: "Fredrik Nygren, Hudiksvalls kommun",
    role: "Metodstödjare",
    image: "/Fredrik.jpg",
    phone: "070-1904386",
    email: "fredrik.nygren@hudiksvall.se",
  },
  {
    name: "Jenny Nilsen, Hudiksvalls kommun",
    role: "Individstödjare",
    image: "/Jenny.jpg",
    phone: "070-2453346",
    email: "jenny.nilsen@hudiksvall.se",
  },
  {
    name: "Lina Brolund, Hudiksvalls kommun",
    role: "Individstödjare",
    image: "/Lina.jpg",
    phone: "072-2318779",
    email: "lina.brolund@hudiksvall.se",
  },
  {
    name: "My Wallström KAA, Ljusdals kommun",
    role: "Individstödjare",
    image: "/My.jpg",
    email: "my.wallstrom@ljusdal.se",
    phone: "0651- 182 36",
  },
  {
    name: "Josefin Karlsson, Ljusdals kommun",
    role: "Individstödjare",
    image: "/Josefin.jpg",
     email: "Josefin.karlsson@ljusdal.se",
    phone: " 070-202 41 32",
  },
  {
    name: "Pär Karlsson, Nordanstigs kommun",
    role: "Individstödjare",
    image: "/Pär.jpg",
    email: "par.karlsson@nordanstig.se",
    phone: "072-534 17 00",
  },
];

/* =========================================================
   SHARED COMPONENTS
========================================================= */

function Section({ children, className = "", id }) {
  return (
    <section
      id={id}
      className={`
        px-5
        py-16

        sm:px-6
        sm:py-20

        md:px-10
        md:py-24

        lg:px-16
        lg:py-28

        ${className}
      `}
    >
      <div className="mx-auto max-w-6xl">
        {children}
      </div>
    </section>
  );
}

/* =========================================================
   EYEBROW
========================================================= */

function Eyebrow({ children, light = false }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`
          h-px
          w-7
          shrink-0

          sm:w-8

          ${light ? "bg-[#6ED1C5]" : "bg-[#3BAFA3]"}
        `}
      />

      <span
        className={`
          text-[10px]
          font-semibold
          uppercase
          tracking-[0.18em]

          sm:text-[11px]
          sm:tracking-[0.20em]

          ${light ? "text-[#6ED1C5]" : "text-[#3BAFA3]"}
        `}
      >
        {children}
      </span>
    </div>
  );
}

/* =========================================================
   QUESTION LINK
========================================================= */

function QuestionLink({ number, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        group
        flex
        min-h-[62px]
        w-full
        items-center
        gap-3
        border-t
        border-black/10
        py-4
        text-left
        transition-all
        duration-300

        sm:gap-5
        sm:py-4

        hover:pl-2
        sm:hover:pl-3

        focus:outline-none
        focus-visible:bg-[#E8F7F5]/50
      "
    >
      <span
        className="
          w-6
          shrink-0
          text-[9px]
          font-semibold
          tracking-[0.16em]
          text-neutral-400

          sm:w-7
          sm:text-[10px]
        "
      >
        {number}
      </span>

      <span
        className="
          min-w-0
          flex-1
          text-[15px]
          font-semibold
          leading-6
          tracking-[-0.015em]
          text-neutral-900

          sm:text-base

          md:text-lg
        "
      >
        {title}
      </span>

      <FaArrowRight
        size={11}
        className="
          shrink-0
          text-[#3BAFA3]
          transition-transform
          duration-300

          group-hover:translate-x-1.5
        "
      />
    </button>
  );
}

/* =========================================================
   ACCORDION
========================================================= */

function Accordion({ title, children, open, onClick }) {
  return (
    <div className="border-t border-black/10">
      <button
        type="button"
        onClick={onClick}
        className="
          flex
          min-h-[68px]
          w-full
          items-center
          justify-between
          gap-4
          py-5
          text-left

          sm:gap-6
          sm:py-6

          focus:outline-none
        "
      >
        <span
          className="
            min-w-0
            pr-2
            text-[15px]
            font-semibold
            leading-6
            tracking-[-0.015em]
            text-neutral-900

            sm:text-base

            md:text-lg
          "
        >
          {title}
        </span>

        <span
          className={`
            flex
            h-9
            w-9
            shrink-0
            items-center
            justify-center
            rounded-full
            bg-[#E8F7F5]
            text-[#3BAFA3]
            transition-transform
            duration-300

            ${open ? "rotate-180" : ""}
          `}
        >
          <FaChevronDown size={10} />
        </span>
      </button>

      <div
        className={`
          grid
          transition-all
          duration-300

          ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}
        `}
      >
        <div className="overflow-hidden">
          <div
            className="
              max-w-3xl
              pb-6
              pr-0
              text-[15px]
              leading-7
              text-neutral-600

              sm:pr-8
            "
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   STEP
========================================================= */

function Step({ number, title, text }) {
  return (
    <div
      className="
        grid
        grid-cols-[44px_1fr]
        gap-4

        sm:grid-cols-[48px_1fr]
        sm:gap-5
      "
    >
      <div
        className="
          flex
          h-11
          w-11
          items-center
          justify-center
          rounded-2xl
          bg-[#E8F7F5]
          text-sm
          font-semibold
          text-[#3BAFA3]

          sm:h-12
          sm:w-12
        "
      >
        {number}
      </div>

      <div className="min-w-0">
        <h3
          className="
            text-lg
            font-semibold
            leading-6
            tracking-[-0.02em]
            text-neutral-900
          "
        >
          {title}
        </h3>

        <p
          className="
            mt-2
            text-[15px]
            leading-7
            text-neutral-600
          "
        >
          {text}
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   ACTIVITY
========================================================= */

function Activity({
  number,
  title,
  text,
  href,
  image,
  onImageClick,
}) {
  const isExternal = href?.startsWith("http");

  return (
    <div className="group min-w-0">
      <button
        type="button"
        onClick={onImageClick}
        className="
          block
          w-full
          text-left
          focus:outline-none
        "
        aria-label={`Visa bild för ${title}`}
      >
        <div
          className="
            relative
            aspect-[4/3]
            overflow-hidden
            rounded-[22px]
            bg-[#E8F7F5]

            sm:rounded-[26px]
          "
        >
          {image ? (
            <img
              src={image}
              alt=""
              className="
                h-full
                w-full
                object-cover
                transition-transform
                duration-700

                group-hover:scale-105
              "
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <span
                className="
                  text-5xl
                  font-semibold
                  tracking-[-0.06em]
                  text-[#3BAFA3]/20
                "
              >
                Rm
              </span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

          <div
            className="
              absolute
              bottom-4
              left-4
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              bg-white/90
              text-xs
              font-semibold
              text-[#3BAFA3]
              backdrop-blur

              sm:bottom-5
              sm:left-5
            "
          >
            {number}
          </div>
        </div>
      </button>

      <a
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noreferrer" : undefined}
        className="
          mt-5
          flex
          items-start
          justify-between
          gap-4

          sm:gap-5
        "
      >
        <div className="min-w-0">
          <h3
            className="
              text-lg
              font-semibold
              leading-6
              tracking-[-0.02em]
              text-neutral-900
            "
          >
            {title}
          </h3>

          <p
            className="
              mt-2
              max-w-sm
              text-sm
              leading-6
              text-neutral-500
            "
          >
            {text}
          </p>
        </div>

        <FaArrowRight
          size={12}
          className="
            mt-1.5
            shrink-0
            text-[#3BAFA3]
            transition-transform
            duration-300

            group-hover:translate-x-1.5
          "
        />
      </a>
    </div>
  );
}

/* =========================================================
   PAGE
========================================================= */

export default function DeltagarePage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [showBrochure, setShowBrochure] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-neutral-900">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="relative z-30 border-b border-black/5 bg-white">
        <div
          className="
            mx-auto
            flex
            max-w-6xl
            items-center
            justify-between
            gap-4
            px-5
            py-3

            sm:px-6
            sm:py-4

            md:px-10
            md:py-5

            lg:px-16
            lg:py-6
          "
        >
         {/* LOGO */}
<Link
  to="/"
  className="group flex min-w-0 shrink items-center"
  aria-label="Räkna med mig – startsida"
>
  <img
    src="/icon.png"
    alt="Räkna med mig"
    className="
      h-[120px]
      md:h-[150px]
      w-auto
      max-w-full
      object-contain
      transition-transform
      duration-300
      group-hover:scale-[1.03]
    "
  />
</Link>
          {/* KONTAKTA OSS */}

          <a
            href="#kontakt"
            className="
              group
              inline-flex
              shrink-0
              items-center
              gap-2
              rounded-full
              bg-[#3BAFA3]
              px-4
              py-2.5
              text-xs
              font-semibold
              text-white
              shadow-[0_5px_18px_rgba(59,175,163,0.14)]
              transition-all
              duration-300

              sm:gap-2.5
              sm:px-5
              sm:py-3
              sm:text-sm

              md:px-6

              hover:-translate-y-0.5
              hover:bg-[#31988e]
              hover:shadow-[0_10px_28px_rgba(59,175,163,0.20)]
            "
          >
            <span>Kontakta oss</span>

            <FaArrowRight
              size={10}
              className="
                transition-transform
                duration-300

                group-hover:translate-x-1
              "
            />
          </a>
        </div>
      </header>


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative overflow-hidden bg-white">
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            pb-16
            pt-12

            sm:px-6
            sm:pb-20
            sm:pt-14

            md:px-10
            md:pb-24
            md:pt-16

            lg:px-16
            lg:pb-28
            lg:pt-20
          "
        >
          <div
            className="
              grid
              gap-12

              md:gap-14

              lg:grid-cols-[1.15fr_0.65fr]
              lg:items-end
              lg:gap-20
            "
          >

            {/* =================================================
                MAIN HERO
            ================================================= */}

            <div className="relative min-w-0">

              <Eyebrow>
                För dig som är ung eller närstående
              </Eyebrow>

              <h1
                className="
                  mt-5
                  max-w-[820px]
                  text-[40px]
                  font-semibold
                  leading-[0.98]
                  tracking-[-0.055em]
                  text-neutral-900

                  sm:mt-6
                  sm:text-[48px]

                  md:text-[62px]

                  lg:text-[72px]

                  xl:text-[78px]
                "
              >
                Du behöver inte veta
                <span className="block text-[#3BAFA3]">
                  vad nästa steg är.
                </span>
              </h1>

              <p
                className="
                  mt-6
                  max-w-[600px]
                  text-[17px]
                  leading-7
                  text-neutral-600

                  sm:mt-7
                  sm:text-lg
                  sm:leading-8
                "
              >
                Funderar du på arbete eller studier, men vet inte hur du ska
                komma vidare?
              </p>

              <p
                className="
                  mt-4
                  max-w-[600px]
                  text-[15px]
                  leading-7
                  text-neutral-500

                  sm:text-[16px]
                "
              >
                Räkna med mig kan vara för dig som är 19–29 år och har
                aktivitetsersättning. Tillsammans tar vi reda på vad du vill,
                vad som fungerar för dig och vilket stöd du behöver.
              </p>

            </div>


            {/* =================================================
                HERO QUOTE
            ================================================= */}

            <div
              className="
                relative
                rounded-[24px]
                bg-[#F8FAFA]
                p-6

                sm:rounded-[28px]
                sm:p-7

                md:p-8
              "
            >
              <div className="flex gap-4 sm:gap-5">

                <div className="mt-1 h-12 w-px shrink-0 bg-[#3BAFA3]" />

                <div className="min-w-0">

                  <p
                    className="
                      text-lg
                      font-medium
                      leading-7
                      tracking-[-0.025em]
                      text-neutral-900

                      sm:text-xl
                      sm:leading-8
                    "
                  >
                    Du behöver inte ha en färdig plan när du hör av dig.
                  </p>

                  <p
                    className="
                      mt-4
                      text-sm
                      leading-6
                      text-neutral-500

                      sm:mt-5
                    "
                  >
                    Vi börjar där du är och tar nästa steg tillsammans.
                  </p>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* =====================================================
          QUESTION NAVIGATION
      ===================================================== */}

      <section className="border-y border-black/5 bg-[#F8FAFA]">
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-10

            sm:px-6
            sm:py-12

            md:px-10
            md:py-16

            lg:px-16
          "
        >
          <div
            className="
              grid
              gap-8

              md:gap-10

              lg:grid-cols-[0.55fr_1fr]
              lg:gap-20
            "
          >
            <div>

              <Eyebrow>
                Vad vill du veta?
              </Eyebrow>

              <h2
                className="
                  mt-5
                  text-3xl
                  font-semibold
                  leading-[1.05]
                  tracking-[-0.04em]
                  text-neutral-900

                  sm:text-4xl
                "
              >
                Börja där det känns rätt för dig.
              </h2>

              <p
                className="
                  mt-4
                  max-w-md
                  text-[15px]
                  leading-7
                  text-neutral-600
                "
              >
                Du behöver inte läsa allt. Välj en fråga och läs mer om just
                det du funderar på.
              </p>

            </div>

            <div>

              <QuestionLink
                number="01"
                title="Kan Räkna med mig vara för mig?"
                onClick={() => scrollTo("for-mig")}
              />

              <QuestionLink
                number="02"
                title="Vilket stöd kan jag få?"
                onClick={() => scrollTo("stod")}
              />

              <QuestionLink
                number="03"
                title="Hur går det till i början?"
                onClick={() => scrollTo("sa-gar-det-till")}
              />

              <QuestionLink
                number="04"
                title="Vad är aktivitetsersättning?"
                onClick={() => scrollTo("aktivitetsersattning")}
              />

              <QuestionLink
                number="05"
                title="För dig som är närstående"
                onClick={() => scrollTo("narstaende")}
              />

              <QuestionLink
                number="06"
                title="Vanliga frågor"
                onClick={() => scrollTo("vanliga-fragor")}
              />

            </div>
          </div>
        </div>
      </section>


      {/* =====================================================
          FOR ME
      ===================================================== */}

      <Section id="for-mig" className="bg-white">
        <div
          className="
            grid
            gap-10

            md:gap-14

            lg:grid-cols-[0.55fr_1fr]
            lg:gap-24
          "
        >
          <div>

            <Eyebrow>
              01
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Börja med dig.
            </h2>

          </div>

          <div
            className="
              max-w-2xl
              space-y-5
              text-[15px]
              leading-7
              text-neutral-600

              sm:space-y-6
              sm:text-[16px]
              sm:leading-8
            "
          >
            <p>
              Räkna med mig vänder sig till dig som är{" "}
              <strong className="font-semibold text-neutral-900">
                19–29 år
              </strong>{" "}
              och har aktivitetsersättning eller håller på att ansöka om den.
            </p>

            <p>
              Du kanske vill börja arbeta eller studera längre fram, men
              behöver stöd för att komma vidare.
            </p>

            <p>
              Du behöver inte veta exakt vad du vill göra. Vi börjar med att
              lära känna dig och prata om vad som är viktigt för dig, vad du är
              intresserad av och vilket stöd du skulle behöva.
            </p>

            <p>
              Sedan tar vi fram en plan tillsammans, i en takt som fungerar för
              dig.
            </p>

            <div className="mt-7 border-l-2 border-[#3BAFA3] pl-5 text-[15px] font-medium leading-7 text-neutral-800 sm:pl-6">
              Är du osäker på om Räkna med mig passar dig? Hör gärna av dig
              ändå, så kan du få veta mer.
            </div>
          </div>
        </div>
      </Section>


      {/* =====================================================
          SUPPORT
      ===================================================== */}

      <Section id="stod" className="bg-[#F8FAFA]">
        <div
          className="
            grid
            gap-10

            md:gap-14

            lg:grid-cols-[0.55fr_1fr]
            lg:gap-24
          "
        >
          <div>

            <Eyebrow>
              02
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Vi utgår från det som fungerar för dig.
            </h2>

          </div>

          <div>

            <p
              className="
                max-w-2xl
                text-[15px]
                leading-7
                text-neutral-600

                sm:text-[16px]
                sm:leading-8
              "
            >
              I Räkna med mig utgår vi från dig, dina intressen och det du vill
              utveckla. Tillsammans tar vi reda på vad som skulle kunna fungera
              för just dig.
            </p>

            <div className="mt-9 space-y-8 sm:mt-10 sm:space-y-9">

              <Step
                number="01"
                title="Utforska"
                text="Hos oss har du möjlighet att prova olika vägar utifrån dina intressen."
              />

              <Step
                number="02"
                title="Hitta något som passar"
                text="Det kan handla om att hitta en aktivitet eller utforska vad du skulle vilja arbeta med eller studera."
              />

              <Step
                number="03"
                title="Prova"
                text="När det känns rätt kan du få stöd att prova en praktikplats eller testa att studera."
              />

              <Step
                number="04"
                title="Ta nästa steg"
                text="Om du har kontakt med flera verksamheter kan vi hjälpa dig att få en tydligare bild av vem som gör vad och planera nästa steg tillsammans."
              />

            </div>

          </div>
        </div>
      </Section>


      {/* =====================================================
          ACTIVITIES
      ===================================================== */}

      <section className="overflow-hidden bg-white">
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-16

            sm:px-6
            sm:py-20

            md:px-10
            md:py-28

            lg:px-16
          "
        >
          <div className="max-w-2xl">

            <Eyebrow>
              Exempel på aktiviteter
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Det kan börja på
              <span className="text-[#3BAFA3]">
                {" "}många olika sätt.
              </span>
            </h2>

            <p
              className="
                mt-5
                text-[15px]
                leading-7
                text-neutral-600

                sm:text-[16px]
                sm:leading-8
              "
            >
              Det finns ingen färdig väg som passar alla. Här är några exempel
              på insatser som har erbjudits i Hudiksvall. Det är just exempel på
              vad som har gjorts – inte en lista över sådant du måste välja
              mellan. Tillsammans pratar vi om vad som skulle kunna passa dig
              och vilka möjligheter som finns där du bor.
            </p>
          </div>

          <div
            className="
              mt-10
              grid
              gap-8

              sm:mt-12

              md:grid-cols-2

              lg:grid-cols-3
            "
          >
            <Activity
              number="01"
              title="Programmeringskurs"
              text="En möjlighet att prova programmering och utveckla nya kunskaper."
              href="https://culprogrammering.se/"
              image="/Info programmeringskurs.jpg"
              onImageClick={() =>
                setSelectedActivity("/Info programmeringskurs.jpg")
              }
            />

            <Activity
              number="02"
              title="Körkortsgrupp"
              text="Stöd och struktur i studier mot ett körkort."
              href="#kontakt"
              image="/Info körkortsstruktur.jpg"
              onImageClick={() =>
                setSelectedActivity("/Info körkortsstruktur.jpg")
              }
            />

            <Activity
              number="03"
              title="Gamingcirkel"
              text="En aktivitet för dig som är intresserad av spel."
              href="#kontakt"
              image="/Info gaming.jpg"
              onImageClick={() =>
                setSelectedActivity("/Info gaming.jpg")
              }
            />
          </div>
        </div>
      </section>


      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}

      <section
        id="sa-gar-det-till"
        className="overflow-hidden bg-[#111827]"
      >
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-16

            sm:px-6
            sm:py-20

            md:px-10
            md:py-28

            lg:px-16
          "
        >

          {/* INTRO */}

          <div
            className="
              grid
              gap-10

              md:gap-12

              lg:grid-cols-[0.85fr_1.15fr]
              lg:items-end
              lg:gap-24
            "
          >
            <div>

              <Eyebrow light>
                03 · Hur går det till?
              </Eyebrow>

              <h2
                className="
                  mt-5
                  text-4xl
                  font-semibold
                  leading-[1.02]
                  tracking-[-0.05em]
                  text-white

                  sm:text-5xl

                  md:text-6xl
                "
              >
                Första steget är bara
                <span className="block text-[#6ED1C5]">
                  ett samtal.
                </span>
              </h2>

            </div>

            <div>

              <p
                className="
                  max-w-xl
                  text-[15px]
                  leading-7
                  text-white/60

                  sm:text-[16px]
                  sm:leading-8
                "
              >
                Du behöver inte veta vad du vill göra eller ha en färdig plan.
                Vi börjar där du är och pratar om vad som känns viktigt för dig,
                vad du vill prova och vilket stöd du behöver.
              </p>

            </div>
          </div>


          {/* STEPS */}

          <div className="mt-12 border-t border-white/10 sm:mt-16">

            <div className="grid md:grid-cols-3">

              {/* STEP 01 */}

              <div
                className="
                  border-b
                  border-white/10
                  py-8

                  md:border-b-0
                  md:border-r
                  md:pr-10
                  md:py-10
                "
              >
                <div className="flex items-center justify-between">

                  <span className="text-xs font-semibold tracking-[0.18em] text-[#6ED1C5]">
                    01
                  </span>

                  <FaArrowRight
                    size={11}
                    className="text-white/25"
                  />

                </div>

                <h3 className="mt-7 text-xl font-semibold tracking-[-0.025em] text-white">
                  Hör av dig
                </h3>

                <p className="mt-4 text-[15px] leading-7 text-white/50">
                  Du kan kontakta oss själv eller tillsammans med någon du
                  känner dig trygg med. Vi börjar med ett enkelt samtal där du
                  får berätta om din situation och vad du funderar på.
                </p>
              </div>


              {/* STEP 02 */}

              <div
                className="
                  border-b
                  border-white/10
                  py-8

                  md:border-b-0
                  md:border-r
                  md:px-10
                  md:py-10
                "
              >
                <div className="flex items-center justify-between">

                  <span className="text-xs font-semibold tracking-[0.18em] text-[#6ED1C5]">
                    02
                  </span>

                  <FaArrowRight
                    size={11}
                    className="text-white/25"
                  />

                </div>

                <h3 className="mt-7 text-xl font-semibold tracking-[-0.025em] text-white">
                  Vi lär känna dig
                </h3>

                <p className="mt-4 text-[15px] leading-7 text-white/50">
                  Tillsammans pratar vi om vad som fungerar för dig, vad du är
                  intresserad av och vad du skulle vilja förändra eller prova.
                  Du behöver inte ha svaren från början.
                </p>
              </div>


              {/* STEP 03 */}

              <div
                className="
                  py-8

                  md:pl-10
                  md:py-10
                "
              >
                <div className="flex items-center justify-between">

                  <span className="text-xs font-semibold tracking-[0.18em] text-[#6ED1C5]">
                    03
                  </span>

                  <span className="h-px w-8 bg-[#6ED1C5]/40" />

                </div>

                <h3 className="mt-7 text-xl font-semibold tracking-[-0.025em] text-white">
                  Vi tar nästa steg tillsammans
                </h3>

                <p className="mt-4 text-[15px] leading-7 text-white/50">
                  När vi vet mer kan vi tillsammans hitta något att prova. Det
                  kan vara en aktivitet, praktik, studier eller något annat som
                  känns relevant för dig.
                </p>
              </div>

            </div>
          </div>


          {/* CLOSING */}

          <div
            className="
              mt-10
              flex
              flex-col
              gap-5
              border-t
              border-white/10
              pt-7

              sm:mt-12
              sm:gap-6
              sm:pt-8

              md:flex-row
              md:items-center
              md:justify-between
            "
          >
            <p
              className="
                max-w-2xl
                text-[15px]
                leading-7
                text-white/45
              "
            >
              Det finns ingen bestämd ordning som passar alla. Vi anpassar
              stödet efter dig och tar ett steg i taget.
            </p>

            <a
              href="#kontakt"
              className="
                group
                inline-flex
                shrink-0
                items-center
                gap-3
                text-sm
                font-semibold
                text-[#6ED1C5]
              "
            >
              Prata med oss

              <FaArrowRight
                size={11}
                className="
                  transition-transform
                  duration-300

                  group-hover:translate-x-1.5
                "
              />
            </a>
          </div>

        </div>
      </section>


      {/* =====================================================
          ACTIVITY COMPENSATION
      ===================================================== */}

      <Section
        id="aktivitetsersattning"
        className="bg-white"
      >
        <div
          className="
            grid
            gap-10

            md:gap-14

            lg:grid-cols-[0.7fr_1fr]
            lg:gap-24
          "
        >
          <div>

            <Eyebrow>
              04
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Vad är aktivitetsersättning?
            </h2>

          </div>

          <div className="max-w-2xl">

            <p
              className="
                text-[15px]
                leading-7
                text-neutral-600

                sm:text-[16px]
                sm:leading-8
              "
            >
              På Försäkringskassans webbplats kan du läsa om vem som kan få
              aktivitetsersättning och hur du ansöker.
            </p>

            <a
              href="https://www.forsakringskassan.se/privatperson/vuxen-med-funktionsnedsattning/aktivitetsersattning-for-unga-vuxna"
              target="_blank"
              rel="noreferrer"
              className="
                group
                mt-6
                inline-flex
                max-w-full
                items-start
                gap-3
                border-b
                border-[#3BAFA3]
                pb-2
                text-sm
                font-semibold
                leading-6
                text-[#3BAFA3]

                sm:mt-7
              "
            >
              <span>
                Aktivitetsersättning för unga vuxna
              </span>

              <FaExternalLinkAlt
                size={10}
                className="mt-1.5 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>

            <div
              className="
                mt-8
                rounded-[22px]
                bg-[#F8FAFA]
                p-6

                sm:mt-10
                sm:rounded-[24px]
                sm:p-8
              "
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#3BAFA3] sm:text-[11px]">
                Viktigt för dig med aktivitetsersättning
              </div>

              <p className="mt-4 text-[15px] leading-7 text-neutral-600">
                Prata med din samordnare på Försäkringskassan innan du börjar
                i en aktivitet, så att du får veta vad som gäller för dig.
              </p>
            </div>

          </div>
        </div>
      </Section>


      {/* =====================================================
          STORY
      ===================================================== */}

      <section className="overflow-hidden bg-[#E8F7F5]">
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-16

            sm:px-6
            sm:py-20

            md:px-10
            md:py-28

            lg:px-16
          "
        >
          <div
            className="
              grid
              gap-10

              md:gap-12

              lg:grid-cols-[0.8fr_1.2fr]
              lg:gap-24
            "
          >

            <div className="relative">

              <div
                className="
                  pointer-events-none
                  absolute
                  -left-5
                  -top-16
                  text-[160px]
                  font-semibold
                  leading-none
                  tracking-[-0.12em]
                  text-[#3BAFA3]/10

                  sm:-left-10
                  sm:-top-20
                  sm:text-[200px]

                  md:text-[240px]
                "
              >
                “
              </div>

              <div className="relative">

                <Eyebrow>
                  En berättelse
                </Eyebrow>

                <h2
                  className="
                    mt-5
                    text-4xl
                    font-semibold
                    leading-[1.05]
                    tracking-[-0.05em]
                    text-neutral-900

                    sm:text-5xl
                  "
                >
                  Ibland börjar förändringen med ett samtal.
                </h2>

                <p
                  className="
                    mt-5
                    max-w-md
                    text-[15px]
                    leading-7
                    text-neutral-600

                    sm:mt-6
                    sm:text-[16px]
                    sm:leading-8
                  "
                >
                  En berättelse kan ibland säga mer än många ord. Här får du
                  möta en mamma och hennes son och höra om deras erfarenheter.
                </p>

              </div>
            </div>


            <div className="max-w-2xl">

              <div
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-[22px]
                  bg-black
                  shadow-[0_24px_70px_rgba(0,0,0,0.12)]

                  sm:rounded-[28px]
                "
              >
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full object-cover"
                >
                  <source
                    src="/Mammas berättelse.mp4"
                    type="video/mp4"
                  />

                  Din webbläsare kan inte spela upp filmen.
                </video>
              </div>


              <div
                className="
                  mt-6
                  text-[16px]
                  leading-7
                  text-neutral-700

                  sm:mt-7
                  sm:text-[17px]
                  sm:leading-8
                "
              >
                <p>
                  Här kommer en berättelse om en mamma och hennes son.
                </p>

                <p className="mt-5">
                  Berättelsen kan beskriva hur det var innan kontakten med
                  Räkna med mig, vad som förändrades och hur små steg kunde
                  göra det möjligt att komma vidare.
                </p>

                <p className="mt-5">
                  Tanken är att visa att vägen framåt inte behöver vara rak.
                  Man behöver inte veta från början vart man ska.
                </p>
              </div>


              <div className="mt-7 border-l-2 border-[#3BAFA3] pl-5 sm:mt-8 sm:pl-6">

                <p
                  className="
                    text-lg
                    font-semibold
                    leading-7
                    tracking-[-0.02em]
                    text-neutral-900

                    sm:text-lg
                    sm:leading-8
                  "
                >
                  “Det viktigaste var kanske inte att veta vart han skulle.
                  Det var att någon hjälpte honom att börja.”
                </p>

              </div>

            </div>
          </div>
        </div>
      </section>


      {/* =====================================================
          RELATIVES
      ===================================================== */}

      <Section
        id="narstaende"
        className="bg-white"
      >
        <div
          className="
            grid
            gap-10

            md:gap-14

            lg:grid-cols-[0.6fr_1fr]
            lg:gap-24
          "
        >
          <div>

            <Eyebrow>
              05
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Du kan finnas med utan att behöva bära allt.
            </h2>

          </div>

          <div
            className="
              max-w-2xl
              text-[15px]
              leading-7
              text-neutral-600

              sm:text-[16px]
              sm:leading-8
            "
          >
            <p>
              Du som är förälder eller annan närstående kan vara ett stöd i att
              ta en första kontakt med Räkna med mig eller följa med på ett
              första samtal, om den unga vill det.
            </p>

            <p className="mt-5">
              Stödet utgår alltid från den ungas egna mål och önskemål.
            </p>


            <div className="mt-8 border-t border-black/10 pt-7 sm:mt-9">

              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#3BAFA3] sm:text-xs">
                Cirkel för närstående
              </div>

              <h3
                className="
                  mt-3
                  text-2xl
                  font-semibold
                  leading-7
                  tracking-[-0.03em]
                  text-neutral-900
                "
              >
                Träffa andra som har liknande erfarenheter.
              </h3>

              <p className="mt-4 text-[15px] leading-7 text-neutral-600">
                Vi erbjuder också en cirkel för närstående. Det är en möjlighet
                att träffa andra som har liknande erfarenheter, dela tankar och
                prata om sådant som är viktigt för er. Ni formar samtalen
                tillsammans utifrån det ni vill ta upp.
              </p>

              <p className="mt-5 text-[15px] leading-7 text-neutral-600">
                Om du är intresserad eller vill veta mer, kontakta{" "}
                <strong className="font-semibold text-neutral-900">
                  Fredrik Nygren
                </strong>
                .
              </p>

            </div>
          </div>
        </div>
      </Section>


      {/* =====================================================
          FAQ
      ===================================================== */}

      <Section
        id="vanliga-fragor"
        className="bg-[#F8FAFA]"
      >
        <div className="mx-auto max-w-3xl">

          <div className="mb-7 sm:mb-9">

            <Eyebrow>
              06
            </Eyebrow>

            <h2
              className="
                mt-5
                text-4xl
                font-semibold
                leading-[1.05]
                tracking-[-0.05em]
                text-neutral-900

                sm:text-5xl
              "
            >
              Det är helt okej att undra.
            </h2>

            <p
              className="
                mt-4
                max-w-2xl
                text-[16px]
                leading-7
                text-neutral-600

                sm:text-lg
                sm:leading-8
              "
            >
              Här har vi samlat några frågor som många funderar på.
            </p>

          </div>


          <div className="border-b border-black/10">

            <Accordion
              title="Måste jag veta vad jag vill arbeta med eller studera?"
              open={openFaq === 0}
              onClick={() =>
                setOpenFaq(openFaq === 0 ? null : 0)
              }
            >
              Nej. Vi börjar med att lära känna varandra. Du behöver inte ha en
              färdig plan.
            </Accordion>


            <Accordion
              title="Måste min planering innefatta aktiviteter på heltid?"
              open={openFaq === 1}
              onClick={() =>
                setOpenFaq(openFaq === 1 ? null : 1)
              }
            >
              Nej. Vi planerar tillsammans utifrån vad som fungerar för dig
              just nu.
            </Accordion>


            <Accordion
              title="Jag är osäker på om Räkna med mig passar, kan jag höra av mig ändå?"
              open={openFaq === 2}
              onClick={() =>
                setOpenFaq(openFaq === 2 ? null : 2)
              }
            >
              Ja. Du kan kontakta oss och ställa frågor, så pratar vi om vilket
              stöd du behöver och om Räkna med mig kan vara aktuellt.
            </Accordion>


            <Accordion
              title="Kan jag ta med någon till det första samtalet?"
              open={openFaq === 3}
              onClick={() =>
                setOpenFaq(openFaq === 3 ? null : 3)
              }
            >
              Ja. Du kan ta med en förälder eller någon annan du känner dig
              trygg med.
            </Accordion>


            <Accordion
              title="Påverkas min aktivitetsersättning om jag deltar?"
              open={openFaq === 4}
              onClick={() =>
                setOpenFaq(openFaq === 4 ? null : 4)
              }
            >
              Nej, men det är viktigt att din samordnare på Försäkringskassan är
              involverad i planeringen.
            </Accordion>

          </div>
        </div>
      </Section>


      {/* =====================================================
          BROCHURE
      ===================================================== */}

      <section className="overflow-hidden bg-[#E8F7F5]">
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-16

            sm:px-6
            sm:py-20

            md:px-10
            md:py-28

            lg:px-16
          "
        >

          <div
            className="
              grid
              gap-8

              lg:grid-cols-[1fr_auto]
              lg:items-center
              lg:gap-10
            "
          >

            <div>

              <Eyebrow>
                Din kontakt med oss
              </Eyebrow>

              <h2
                className="
                  mt-5
                  max-w-2xl
                  text-4xl
                  font-semibold
                  leading-[1.05]
                  tracking-[-0.05em]
                  text-neutral-900

                  sm:text-5xl
                "
              >
                Vill du veta hur vi tänker kring möten?              </h2>

              <p
                className="
                  mt-5
                  max-w-xl
                  text-[15px]
                  leading-7
                  text-neutral-600

                  sm:text-[16px]
                  sm:leading-8
                "
              >
                I broschyren kan du läsa mer om det. 
              </p>

            </div>


            <button
              type="button"
              onClick={() => setShowBrochure(true)}
              className="
                group
                inline-flex
                w-full
                items-center
                justify-center
                gap-3
                rounded-full
                bg-[#3BAFA3]
                px-6
                py-3.5
                text-sm
                font-semibold
                text-white
                shadow-[0_8px_25px_rgba(59,175,163,0.16)]
                transition-all
                duration-300

                sm:w-auto

                hover:-translate-y-0.5
                hover:bg-[#31988e]
                hover:shadow-[0_12px_30px_rgba(59,175,163,0.22)]
              "
            >
              Läs broschyren

              <FaArrowRight
                size={11}
                className="
                  transition-transform
                  duration-300

                  group-hover:translate-x-1
                "
              />
            </button>

          </div>
        </div>
      </section>


      {/* =====================================================
          CONTACT
      ===================================================== */}

      <section
        id="kontakt"
        className="bg-[#111827] text-white"
      >
        <div
          className="
            mx-auto
            max-w-6xl
            px-5
            py-16

            sm:px-6
            sm:py-20

            md:px-10
            md:py-28

            lg:px-16
          "
        >

          {/* INTRO */}

          <div
            className="
              grid
              gap-10

              md:gap-12

              lg:grid-cols-[0.8fr_1.2fr]
              lg:items-end
              lg:gap-24
            "
          >

            <div>

              <Eyebrow light>
                Kontakt
              </Eyebrow>

              <h2
                className="
                  mt-5
                  text-4xl
                  font-semibold
                  leading-[1.05]
                  tracking-[-0.05em]

                  sm:text-5xl

                  md:text-6xl
                "
              >
                Ska vi ta ett
                <span className="block text-[#6ED1C5]">
                  första samtal?
                </span>
              </h2>

            </div>

            <div>

              <p
                className="
                  max-w-xl
                  text-[15px]
                  leading-7
                  text-white/60

                  sm:text-[16px]
                  sm:leading-8
                "
              >
                Vill du veta mer om Räkna med mig, våra arbetssätt eller hur vi
                samverkar? Hör gärna av dig till någon av oss.
              </p>

            </div>

          </div>


          {/* TEAM */}

          <div className="mt-12 border-t border-white/10 pt-8 sm:mt-16 sm:pt-10">

            <div className="mb-7 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35 sm:text-xs">
              Kontakta oss
            </div>

            <div
              className="
                grid
                gap-5

                sm:grid-cols-2
                sm:gap-6

                lg:grid-cols-3
                lg:gap-x-8
                lg:gap-y-10
              "
            >

              {teamet.map((person) => (
                <div
                  key={person.name}
                  className="
                    group
                    flex
                    min-w-0
                    items-center
                    gap-4
                    rounded-[20px]
                    border
                    border-white/10
                    bg-white/[0.03]
                    p-4
                    transition-all
                    duration-300

                    sm:gap-5
                    sm:rounded-[22px]
                    sm:p-5

                    hover:border-[#6ED1C5]/30
                    hover:bg-white/[0.05]
                  "
                >

                  {/* BILD */}

                  <div
                    className="
                      h-16
                      w-16
                      shrink-0
                      overflow-hidden
                      rounded-full
                      bg-white/10

                      sm:h-20
                      sm:w-20
                    "
                  >
                    {person.image ? (
                      <img
                        src={person.image}
                        alt={person.name}
                        className="
                          h-full
                          w-full
                          object-cover
                          transition-transform
                          duration-500

                          group-hover:scale-105
                        "
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <span className="text-xl font-semibold text-[#6ED1C5]/40">
                          Rm
                        </span>
                      </div>
                    )}
                  </div>


                  {/* NAMN + ROLL + KONTAKT */}

                  <div className="min-w-0 flex-1">

                    <h3
                      className="
                        break-words
                        text-sm
                        font-semibold
                        leading-5
                        text-white

                        sm:text-base
                      "
                    >
                      {person.name}
                    </h3>

                    {person.role ? (
                      <p className="mt-1 text-xs leading-5 text-[#6ED1C5] sm:text-sm">
                        {person.role}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-white/35">
                        Räkna med mig
                      </p>
                    )}

                    <div className="mt-2.5 flex min-w-0 flex-col gap-1">

                      {person.phone && (
                        <a
                          href={`tel:${person.phone.replace(/\s/g, "")}`}
                          className="
                            text-xs
                            text-white/55
                            transition-colors
                            hover:text-[#6ED1C5]
                          "
                        >
                          {person.phone}
                        </a>
                      )}

                      {person.email && (
                        <a
                          href={`mailto:${person.email}`}
                          className="
                            break-all
                            text-[11px]
                            leading-4
                            text-white/55
                            transition-colors
                            hover:text-[#6ED1C5]

                            sm:text-xs
                          "
                        >
                          {person.email}
                        </a>
                      )}

                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>


          {/* KOMMUNER */}

          <div className="mt-10 border-t border-white/10 pt-7 sm:mt-12 sm:pt-8">

            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35 sm:text-xs">
              Projektets kommuner
            </div>

            <div className="mt-4 flex flex-wrap gap-2.5">

              {["Hudiksvall", "Nordanstig", "Ljusdal"].map((kommun) => (
                <span
                  key={kommun}
                  className="
                    rounded-full
                    border
                    border-white/10
                    px-4
                    py-2
                    text-xs
                    font-medium
                    text-white/60

                    sm:text-sm
                  "
                >
                  {kommun}
                </span>
              ))}

            </div>
          </div>

        </div>
      </section>


      {/* =====================================================
          FOOTER
      ===================================================== */}

      <footer className="border-t border-white/10 bg-neutral-950 text-white">

        <div
          className="
            mx-auto
            flex
            max-w-6xl
            flex-col
            gap-2
            px-5
            py-6
            text-xs
            text-white/35

            sm:px-6
            sm:text-sm

            md:flex-row
            md:items-center
            md:justify-between
            md:px-10

            lg:px-16
          "
        >

          <p>
            © Räkna med mig
          </p>

          <p>
            Hudiksvall · Ljusdal · Nordanstig
          </p>

        </div>
      </footer>


      {/* =====================================================
          BROCHURE MODAL
      ===================================================== */}

      {showBrochure && (
        <div
          className="
            fixed
            inset-0
            z-50
            overflow-y-auto
            bg-black/80
            px-3
            py-5
            backdrop-blur-sm

            sm:px-4
            sm:py-8
          "
          onClick={() => setShowBrochure(false)}
        >

          <div
            className="
              mx-auto
              w-full
              max-w-5xl
            "
            onClick={(e) => e.stopPropagation()}
          >

            {/* MODAL HEADER */}

            <div
              className="
                mb-4
                flex
                items-start
                justify-between
                gap-4
                px-1

                sm:mb-6
                sm:px-2
              "
            >

              <div className="min-w-0">

                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6ED1C5] sm:text-xs">
                  Din kontakt med oss
                </div>

                <h2
                  className="
                    mt-2
                    text-xl
                    font-semibold
                    leading-6
                    tracking-[-0.03em]
                    text-white

                    sm:text-2xl
                    sm:leading-7
                  "
                >
                  Vad händer när du tar kontakt?
                </h2>

              </div>


              <button
                type="button"
                onClick={() => setShowBrochure(false)}
                aria-label="Stäng broschyren"
                className="
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-white/10
                  text-xl
                  text-white
                  transition-colors

                  sm:h-11
                  sm:w-11

                  hover:bg-white/20
                "
              >
                ×
              </button>

            </div>


            {/* BROCHURE IMAGES */}

            <div className="space-y-4 sm:space-y-6">

              <img
                src="/1.jpg"
                alt="Broschyr om att ta kontakt med Räkna med mig, sida 1"
                className="
                  block
                  w-full
                  rounded-[14px]
                  bg-white
                  shadow-2xl

                  sm:rounded-[18px]
                "
              />

              <img
                src="/2.jpg"
                alt="Broschyr om att ta kontakt med Räkna med mig, sida 2"
                className="
                  block
                  w-full
                  rounded-[14px]
                  bg-white
                  shadow-2xl

                  sm:rounded-[18px]
                "
              />

            </div>


            <div className="py-6 text-center text-xs text-white/40 sm:py-8 sm:text-sm">
              Klicka utanför för att stänga
            </div>

          </div>
        </div>
      )}


      {/* =====================================================
          ACTIVITY IMAGE MODAL
      ===================================================== */}

      {selectedActivity && (
        <div
          className="
            fixed
            inset-0
            z-50
            flex
            items-center
            justify-center
            bg-black/85
            p-3
            backdrop-blur-sm

            sm:p-6

            md:p-8
          "
          onClick={() => setSelectedActivity(null)}
        >

          <div
            className="
              relative
              flex
              max-h-full
              max-w-6xl
              items-center
              justify-center
            "
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              onClick={() => setSelectedActivity(null)}
              aria-label="Stäng bild"
              className="
                absolute
                -right-1
                -top-1
                z-10
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-full
                bg-white
                text-xl
                text-neutral-900
                shadow-lg
                transition-transform

                sm:-right-2
                sm:-top-2

                hover:scale-105
              "
            >
              ×
            </button>


            <img
              src={selectedActivity}
              alt="Aktivitet"
              className="
                max-h-[88vh]
                max-w-full
                rounded-[14px]
                object-contain
                shadow-2xl

                sm:max-h-[90vh]
                sm:rounded-[18px]
              "
            />

          </div>
        </div>
      )}

    </div>
  );
}